import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { ErrorToast } from './ErrorToast';

export interface Toast {
  id: string;
  message: string;
  type?: 'error' | 'warning' | 'info';
  actionLabel?: string;
  onAction?: () => void;
  autoClose?: boolean;
}

interface ToastContextValue {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  showError: (message: string, actionLabel?: string, onAction?: () => void) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const showError = useCallback((message: string, actionLabel?: string, onAction?: () => void) => {
    showToast({
      message,
      type: 'error',
      actionLabel,
      onAction,
      autoClose: !actionLabel, // Don't auto-close if there's an action
    });
  }, [showToast]);

  const showWarning = useCallback((message: string) => {
    showToast({
      message,
      type: 'warning',
    });
  }, [showToast]);

  const showInfo = useCallback((message: string) => {
    showToast({
      message,
      type: 'info',
    });
  }, [showToast]);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showError, showWarning, showInfo, clearToasts }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <ErrorToast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            actionLabel={toast.actionLabel}
            onAction={toast.onAction}
            onClose={() => removeToast(toast.id)}
            autoClose={toast.autoClose}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
